import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  LogOut, Users, Phone, MessageCircle, Trash2, Plus, X, Search,
  IndianRupee, CalendarClock, UserPlus, ChevronRight, PhoneCall, StickyNote,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { LogoLockup } from "@/components/dw/Logo";
import { ApplicationsTab } from "@/components/dw/ApplicationsTab";
import { AllPagesTab } from "@/components/dw/AllPagesTab";
import ShowcaseCRM from "@/pages/showcase/ShowcaseCRM";

const STATUS_COLORS = {
  active: { bg: "#DEF7DA", fg: "#2C633D" },
  paused: { bg: "#FFF3C4", fg: "#8B6D00" },
  completed: { bg: "#E8EAF7", fg: "#4A4A8A" },
  lost: { bg: "#F7E0E0", fg: "#8F2929" },
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("clients");
  const [summary, setSummary] = useState({ clients: { total: 0, active: 0 }, revenue_this_month: {}, followups: { this_week: 0, overdue: 0 }, leads: { open: 0 } });
  const [leads, setLeads] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientQ, setClientQ] = useState("");
  const [clientStatus, setClientStatus] = useState("");
  const [blogs, setBlogs] = useState([]);
  const [showNewBlog, setShowNewBlog] = useState(false);
  const [showNewClient, setShowNewClient] = useState(false);
  const [openClientId, setOpenClientId] = useState(null);
  const [convertLeadId, setConvertLeadId] = useState(null);
  const [showcase, setShowcase] = useState(() => sessionStorage.getItem("dw_view") === "1");
  const toggleShowcase = () => setShowcase((v) => { sessionStorage.setItem("dw_view", v ? "0" : "1"); return !v; });

  useEffect(() => {
    api.get("/auth/me").then((r) => { setMe(r.data); setChecking(false); }).catch(() => nav("/admin/login"));
  }, [nav]);

  const loadSummary = useCallback(() => api.get("/admin/summary").then((r) => setSummary(r.data)).catch(() => {}), []);
  const loadLeads = useCallback(() => api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {}), []);
  const loadClients = useCallback(() => {
    const params = new URLSearchParams();
    if (clientQ) params.set("q", clientQ);
    if (clientStatus) params.set("status", clientStatus);
    api.get(`/admin/clients?${params.toString()}`).then((r) => setClients(r.data)).catch(() => {});
  }, [clientQ, clientStatus]);
  const loadBlogs = useCallback(() => api.get("/blog").then((r) => setBlogs(r.data)).catch(() => {}), []);

  useEffect(() => { if (me) { loadSummary(); loadClients(); loadLeads(); loadBlogs(); } }, [me, loadSummary, loadClients, loadLeads, loadBlogs]);
  useEffect(() => { if (me) loadClients(); }, [clientQ, clientStatus, loadClients, me]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    sessionStorage.removeItem("dw_view");
    nav("/admin/login");
  };
  const removeLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try { await api.delete(`/admin/leads/${id}`); toast.success("Deleted"); loadLeads(); loadSummary(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const removeBlog = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/admin/blog/${id}`); toast.success("Deleted"); loadBlogs(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const removeClient = async (id) => {
    if (!window.confirm("Delete this client and all their payments + notes?")) return;
    try { await api.delete(`/admin/clients/${id}`); toast.success("Client deleted"); setOpenClientId(null); loadClients(); loadSummary(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  if (checking) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;

  const revenueLines = Object.entries(summary.revenue_this_month || {});

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-paper)" }}>
      <header className="border-b sticky top-0 z-30" style={{ background: "var(--bg)", borderColor: "var(--border-subtle)" }}>
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <LogoLockup size={40} />
          </Link>
          <span className="font-display text-lg ml-2 flex-1 flex items-center gap-2">
            <span className="opacity-60 whitespace-nowrap">· CRM</span>
            <button data-testid="crm-switch" role="switch" aria-checked={showcase} onClick={toggleShowcase} className="relative w-9 h-5 rounded-full transition-colors duration-200" style={{ background: showcase ? "var(--primary)" : "var(--border-strong)" }}>
              <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform duration-200" style={{ transform: showcase ? "translateX(16px)" : "translateX(0)" }} />
            </button>
          </span>
          <div className="flex items-center gap-3">
            <span className="text-sm hidden md:inline" style={{ color: "var(--text-secondary)" }}>{me?.email}</span>
            <button data-testid="admin-logout" onClick={logout} className="btn-ghost text-sm">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        {showcase ? <ShowcaseCRM /> : <>
        {/* Summary strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <SumCard title="Active clients" value={summary.clients?.active || 0} sub={`${summary.clients?.total || 0} total`} icon={<Users size={18} />} tint="var(--primary)" />
          <SumCard title="Revenue this month" value={revenueLines.length === 0 ? "—" : revenueLines.map(([cur, v]) => `${cur} ${Math.round(v.total).toLocaleString()}`).join(" · ")} sub={revenueLines.reduce((s, [, v]) => s + v.count, 0) + " payments"} icon={<IndianRupee size={18} />} tint="var(--red)" small />
          <SumCard title="Follow-ups this week" value={summary.followups?.this_week || 0} sub={`${summary.followups?.overdue || 0} overdue`} icon={<CalendarClock size={18} />} tint="var(--yellow-deep)" />
          <SumCard title="Open leads" value={summary.leads?.open || 0} sub="click to convert" icon={<UserPlus size={18} />} tint="var(--primary-light)" />
        </div>

        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <TabBtn active={tab === "clients"} onClick={() => setTab("clients")} testid="tab-clients">Clients</TabBtn>
          <TabBtn active={tab === "leads"} onClick={() => setTab("leads")} testid="tab-leads">Leads {summary.leads?.open ? `(${summary.leads.open})` : ""}</TabBtn>
          <TabBtn active={tab === "blog"} onClick={() => setTab("blog")} testid="tab-blog">Blog</TabBtn>
          <TabBtn active={tab === "applications"} onClick={() => setTab("applications")} testid="tab-applications">Applications</TabBtn>
          <TabBtn active={tab === "pages"} onClick={() => setTab("pages")} testid="tab-all-pages">All Pages</TabBtn>
        </div>

        {tab === "applications" && <ApplicationsTab />}
        {tab === "pages" && <AllPagesTab />}

        {tab === "clients" && (
          <ClientsTab
            clients={clients}
            q={clientQ}
            setQ={setClientQ}
            status={clientStatus}
            setStatus={setClientStatus}
            onNew={() => setShowNewClient(true)}
            onOpen={setOpenClientId}
          />
        )}

        {tab === "leads" && (
          <LeadsTab leads={leads} onRemove={removeLead} onConvert={setConvertLeadId} />
        )}

        {tab === "blog" && (
          <BlogTab blogs={blogs} onNew={() => setShowNewBlog(true)} onRemove={removeBlog} />
        )}
        </>}
      </div>

      {showNewClient && <NewClientModal onClose={() => setShowNewClient(false)} onCreated={() => { setShowNewClient(false); loadClients(); loadSummary(); }} />}
      {showNewBlog && <NewBlogModal onClose={() => setShowNewBlog(false)} onCreated={() => { setShowNewBlog(false); loadBlogs(); }} />}
      {openClientId && (
        <ClientDrawer
          clientId={openClientId}
          onClose={() => setOpenClientId(null)}
          onDelete={removeClient}
          onChanged={() => { loadClients(); loadSummary(); }}
        />
      )}
      {convertLeadId && (
        <ConvertLeadModal
          leadId={convertLeadId}
          onClose={() => setConvertLeadId(null)}
          onDone={() => { setConvertLeadId(null); loadLeads(); loadClients(); loadSummary(); setTab("clients"); }}
        />
      )}
    </div>
  );
}

/* -------------------- Summary card -------------------- */
function SumCard({ title, value, sub, icon, tint, small = false }) {
  return (
    <div className="card-soft p-5">
      <div className="text-xs uppercase tracking-widest font-bold" style={{ color: "var(--text-secondary)" }}>{title}</div>
      <div className="mt-2 flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white" style={{ background: tint }}>{icon}</div>
        <div className={small ? "font-bold text-base" : "font-display text-3xl"} style={{ color: "var(--primary)" }}>{value}</div>
      </div>
      {sub && <div className="mt-1 text-xs" style={{ color: "var(--text-secondary)" }}>{sub}</div>}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className="px-4 py-2 rounded-full text-sm font-bold transition-colors" style={{ background: active ? "var(--primary)" : "transparent", color: active ? "var(--yellow)" : "var(--primary)", border: active ? "2px solid var(--primary)" : "2px solid var(--border-strong)" }}>
      {children}
    </button>
  );
}

const Th = ({ children, className = "" }) => <th className={`p-3 text-xs uppercase tracking-widest font-bold ${className}`} style={{ color: "var(--text-secondary)" }}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`p-3 align-middle ${className}`}>{children}</td>;

function StatusPill({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.active;
  return <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full" style={{ background: c.bg, color: c.fg }}>{status || "—"}</span>;
}

/* -------------------- Clients tab -------------------- */
function ClientsTab({ clients, q, setQ, status, setStatus, onNew, onOpen }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
          <Input data-testid="client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, phone, city..." className="pl-10 h-11 rounded-full" />
        </div>
        <select data-testid="client-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="h-11 px-4 rounded-full text-sm font-semibold border-2 bg-transparent" style={{ borderColor: "var(--border-strong)", color: "var(--primary)" }}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="completed">Completed</option>
          <option value="lost">Lost</option>
        </select>
        <button data-testid="new-client-btn" onClick={onNew} className="btn-primary text-sm"><Plus size={14} /> New client</button>
      </div>
      <div className="card-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead style={{ background: "var(--bg-paper)" }}>
              <tr className="text-left">
                <Th>Name</Th><Th>Phone</Th><Th>City</Th><Th>Plan</Th><Th>Status</Th><Th>Next follow-up</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {clients.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center" style={{ color: "var(--text-secondary)" }}>No clients yet. Convert a lead or click <b>New client</b>.</td></tr>
              )}
              {clients.map((c) => (
                <tr key={c.id} data-testid={`client-row-${c.id}`} onClick={() => onOpen(c.id)} className="border-t cursor-pointer hover:bg-[var(--bg-paper)]/60" style={{ borderColor: "var(--border-subtle)" }}>
                  <Td><span className="font-bold" style={{ color: "var(--primary)" }}>{c.name}</span></Td>
                  <Td><a href={`tel:${c.phone}`} onClick={(e) => e.stopPropagation()} className="font-semibold" style={{ color: "var(--primary)" }}><Phone size={12} className="inline mr-1" />{c.phone}</a></Td>
                  <Td>{c.city || "—"}</Td>
                  <Td>{c.plan_type ? `${c.plan_type.toUpperCase()} · ${c.plan_currency || "INR"} ${c.plan_price || 0}` : "—"}</Td>
                  <Td><StatusPill status={c.status} /></Td>
                  <Td>{c.next_followup ? new Date(c.next_followup).toLocaleDateString() : "—"}</Td>
                  <Td><ChevronRight size={16} style={{ color: "var(--text-secondary)" }} /></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* -------------------- Leads tab -------------------- */
function LeadsTab({ leads, onRemove, onConvert }) {
  return (
    <div className="card-soft overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead style={{ background: "var(--bg-paper)" }}>
            <tr className="text-left">
              <Th>When</Th><Th>Name</Th><Th>Phone</Th><Th>City</Th><Th>Audience</Th><Th>Source</Th><Th>Goal</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {leads.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center" style={{ color: "var(--text-secondary)" }}>No open leads. They&rsquo;ll appear here as soon as someone books.</td></tr>
            )}
            {leads.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-t" style={{ borderColor: "var(--border-subtle)" }}>
                <Td>{new Date(l.created_at).toLocaleString()}</Td>
                <Td>{l.name || "—"}</Td>
                <Td><a href={`tel:${l.phone}`} className="font-bold" style={{ color: "var(--primary)" }}><Phone size={12} className="inline mr-1" />{l.phone}</a></Td>
                <Td>{l.city || "—"}</Td>
                <Td><span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "var(--bg-paper)" }}>{l.audience || "—"}</span></Td>
                <Td><span className="text-xs">{l.source}</span></Td>
                <Td>{l.goal || "—"}</Td>
                <Td className="text-right">
                  <div className="flex gap-2 justify-end">
                    <button data-testid={`lead-convert-${l.id}`} onClick={() => onConvert(l.id)} className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: "var(--yellow)", color: "var(--primary)", border: "1.5px solid var(--primary)" }}>Convert</button>
                    <button data-testid={`lead-del-${l.id}`} onClick={() => onRemove(l.id)} className="text-red-600"><Trash2 size={14} /></button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* -------------------- Blog tab -------------------- */
function BlogTab({ blogs, onNew, onRemove }) {
  return (
    <div>
      <div className="flex justify-end mb-4">
        <button data-testid="blog-new-btn" onClick={onNew} className="btn-primary text-sm"><Plus size={14} /> New post</button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {blogs.map((b) => (
          <div key={b.id} className="card-soft p-6">
            <div className="flex justify-between items-start gap-4">
              <div>
                <div className="font-display text-xl" style={{ color: "var(--primary)" }}>{b.title}</div>
                <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>{b.slug}</div>
              </div>
              <button data-testid={`blog-del-${b.id}`} onClick={() => onRemove(b.id)} className="text-red-600"><Trash2 size={16} /></button>
            </div>
            <p className="mt-3 text-sm line-clamp-2" style={{ color: "var(--text-secondary)" }}>{b.excerpt}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------- New Client Modal -------------------- */
function NewClientModal({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", phone: "", email: "", city: "", audience: "india", plan_type: "m3", plan_price: "", plan_currency: "INR", goal: "", source: "walk-in", status: "active" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.phone) { toast.error("Name and phone are required"); return; }
    setLoading(true);
    try {
      await api.post("/admin/clients", { ...f, plan_price: f.plan_price ? Number(f.plan_price) : null });
      toast.success("Client created");
      onCreated();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return <ClientFormModal title="New client" onClose={onClose} f={f} set={set} loading={loading} onSubmit={submit} />;
}

function ClientFormModal({ title, onClose, f, set, loading, onSubmit }) {
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 overflow-y-auto" style={{ background: "rgba(0,0,0,0.4)" }} onClick={onClose}>
      <div className="w-full max-w-2xl card-soft p-8 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start">
          <div>
            <div className="section-eyebrow">CRM</div>
            <h3 className="font-display text-3xl mt-1" style={{ color: "var(--primary)" }}>{title}</h3>
          </div>
          <button onClick={onClose}><X /></button>
        </div>
        <form onSubmit={onSubmit} className="mt-6 grid md:grid-cols-2 gap-4">
          <Input data-testid="client-name" placeholder="Full name *" value={f.name} onChange={set("name")} required className="h-11 rounded-xl" />
          <Input data-testid="client-phone" placeholder="Phone *" value={f.phone} onChange={set("phone")} required className="h-11 rounded-xl" />
          <Input data-testid="client-email" placeholder="Email" type="email" value={f.email} onChange={set("email")} className="h-11 rounded-xl" />
          <Input data-testid="client-city" placeholder="City" value={f.city} onChange={set("city")} className="h-11 rounded-xl" />
          <SelectField label="Audience" value={f.audience} onChange={set("audience")} testid="client-audience"
            options={[["india", "India"], ["canada", "Canada"], ["usa", "USA"], ["other", "Other"]]} />
          <SelectField label="Source" value={f.source} onChange={set("source")} testid="client-source"
            options={[["walk-in", "Walk-in"], ["lead", "Lead"], ["referral", "Referral"], ["online", "Online"]]} />
          <SelectField label="Plan type" value={f.plan_type} onChange={set("plan_type")} testid="client-plan"
            options={[["m1", "1 month"], ["m3", "3 months"], ["m6", "6 months"], ["custom", "Custom"]]} />
          <div className="flex gap-2">
            <Input data-testid="client-price" placeholder="Price" type="number" value={f.plan_price} onChange={set("plan_price")} className="h-11 rounded-xl flex-1" />
            <select data-testid="client-currency" value={f.plan_currency} onChange={set("plan_currency")} className="h-11 rounded-xl px-3 border-2 bg-transparent" style={{ borderColor: "var(--border-strong)" }}>
              <option>INR</option><option>CAD</option><option>USD</option><option>GBP</option><option>AUD</option><option>AED</option>
            </select>
          </div>
          <SelectField label="Status" value={f.status} onChange={set("status")} testid="client-status"
            options={[["active", "Active"], ["paused", "Paused"], ["completed", "Completed"], ["lost", "Lost"]]} />
          <Input data-testid="client-followup" placeholder="Next follow-up (YYYY-MM-DD)" type="date" value={f.next_followup || ""} onChange={set("next_followup")} className="h-11 rounded-xl" />
          <Input data-testid="client-goal" placeholder="Goal" value={f.goal} onChange={set("goal")} className="h-11 rounded-xl md:col-span-2" />
          <div className="md:col-span-2 flex gap-3 justify-end">
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
            <button data-testid="client-submit" disabled={loading} className="btn-primary disabled:opacity-60">{loading ? "…" : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "var(--text-secondary)" }}>{label}</label>
      <select data-testid={testid} value={value || ""} onChange={onChange} className="mt-1.5 w-full h-11 rounded-xl px-3 border-2 bg-transparent text-sm" style={{ borderColor: "var(--border-strong)" }}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

/* -------------------- Convert Lead Modal -------------------- */
function ConvertLeadModal({ leadId, onClose, onDone }) {
  const [f, setF] = useState({ plan_type: "m3", plan_price: "", plan_currency: "INR", plan_start: "", plan_end: "", goal: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/admin/leads/${leadId}/convert`, { ...f, plan_price: f.plan_price ? Number(f.plan_price) : null });
      toast.success("Lead converted to client");
      onDone();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.4)" }} onClick={onClose}>
      <div className="w-full max-w-md card-soft p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start">
          <div>
            <div className="section-eyebrow">Convert</div>
            <h3 className="font-display text-2xl mt-1" style={{ color: "var(--primary)" }}>Turn this lead into a client</h3>
          </div>
          <button onClick={onClose}><X /></button>
        </div>
        <form onSubmit={submit} className="mt-6 grid grid-cols-2 gap-3">
          <SelectField label="Plan" value={f.plan_type} onChange={(e) => setF({ ...f, plan_type: e.target.value })} testid="convert-plan"
            options={[["m1", "1 month"], ["m3", "3 months"], ["m6", "6 months"], ["custom", "Custom"]]} />
          <div className="flex gap-2">
            <Input placeholder="Price" type="number" value={f.plan_price} onChange={(e) => setF({ ...f, plan_price: e.target.value })} className="h-11 rounded-xl flex-1" />
            <select value={f.plan_currency} onChange={(e) => setF({ ...f, plan_currency: e.target.value })} className="h-11 rounded-xl px-3 border-2 bg-transparent" style={{ borderColor: "var(--border-strong)" }}>
              <option>INR</option><option>CAD</option><option>USD</option><option>GBP</option><option>AUD</option><option>AED</option>
            </select>
          </div>
          <Input type="date" value={f.plan_start} onChange={(e) => setF({ ...f, plan_start: e.target.value })} className="h-11 rounded-xl" placeholder="Start" />
          <Input type="date" value={f.plan_end} onChange={(e) => setF({ ...f, plan_end: e.target.value })} className="h-11 rounded-xl" placeholder="End" />
          <Input placeholder="Goal (optional)" value={f.goal} onChange={(e) => setF({ ...f, goal: e.target.value })} className="h-11 rounded-xl col-span-2" />
          <div className="col-span-2 flex gap-3 justify-end mt-2">
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
            <button data-testid="convert-submit" disabled={loading} className="btn-primary disabled:opacity-60">{loading ? "…" : "Convert"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* -------------------- Client Drawer -------------------- */
function ClientDrawer({ clientId, onClose, onDelete, onChanged }) {
  const [data, setData] = useState(null);
  const [payAmt, setPayAmt] = useState("");
  const [payMethod, setPayMethod] = useState("upi");
  const [payNote, setPayNote] = useState("");
  const [noteType, setNoteType] = useState("call");
  const [noteText, setNoteText] = useState("");
  const [dietitians, setDietitians] = useState([]);
  const [showAppAccess, setShowAppAccess] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/clients/${clientId}`).then((r) => setData(r.data)).catch(() => {});
  }, [clientId]);
  useEffect(() => {
    load();
    api.get("/app/owner/team").then((r) => setDietitians(r.data)).catch(() => {});
  }, [load]);

  const addPayment = async (e) => {
    e.preventDefault();
    if (!payAmt) return toast.error("Amount required");
    try {
      await api.post(`/admin/clients/${clientId}/payments`, {
        amount: Number(payAmt), method: payMethod, note: payNote,
        currency: data?.client?.plan_currency || "INR",
      });
      setPayAmt(""); setPayNote("");
      toast.success("Payment logged");
      load(); onChanged();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const addNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return toast.error("Note text required");
    try {
      await api.post(`/admin/clients/${clientId}/notes`, { type: noteType, text: noteText });
      setNoteText("");
      toast.success("Note added");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const updateStatus = async (status) => {
    try { await api.patch(`/admin/clients/${clientId}`, { status }); toast.success("Status updated"); load(); onChanged(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const assignDietitian = async (dietitian_id) => {
    try { await api.patch(`/admin/clients/${clientId}`, { dietitian_id }); toast.success("Dietitian assigned"); load(); onChanged(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const revokeAccess = async () => {
    if (!window.confirm("Revoke this client's app access? Their login will be deleted.")) return;
    try { await api.delete(`/app/owner/revoke-client-account/${clientId}`); toast.success("App access revoked"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  if (!data) return null;
  const c = data.client;
  const waLink = `https://wa.me/${(c.phone || "").replace(/[^0-9]/g, "")}`;

  return (
    <div className="fixed inset-0 z-[200] flex justify-end" style={{ background: "rgba(0,0,0,0.35)" }} onClick={onClose}>
      <div data-testid="client-drawer" className="w-full max-w-lg h-full overflow-y-auto p-8" style={{ background: "var(--bg)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start">
          <div>
            <div className="section-eyebrow">Client</div>
            <h3 className="font-display text-3xl mt-1" style={{ color: "var(--primary)" }}>{c.name}</h3>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <StatusPill status={c.status} />
              {c.plan_type && <span className="text-xs font-bold px-2 py-1 rounded-full" style={{ background: "var(--yellow)", color: "var(--primary)" }}>{c.plan_type.toUpperCase()}</span>}
              {c.audience && <span className="text-xs px-2 py-1 rounded-full" style={{ background: "var(--bg-paper)" }}>{c.audience}</span>}
              {c.user_id && <span className="text-xs px-2 py-1 rounded-full flex items-center gap-1" style={{ background: "#DEF7DA", color: "#2C633D" }}>📱 App user</span>}
            </div>
          </div>
          <button onClick={onClose}><X /></button>
        </div>

        {/* Quick actions */}
        <div className="mt-6 grid grid-cols-2 gap-3">
          <a href={`tel:${c.phone}`} className="btn-primary text-sm justify-center"><PhoneCall size={14} /> Call</a>
          <a href={waLink} target="_blank" rel="noopener noreferrer" className="btn-ghost text-sm justify-center" style={{ borderColor: "#25D366", color: "#25D366" }}><MessageCircle size={14} /> WhatsApp</a>
        </div>

        {/* Info */}
        <div className="mt-8 space-y-2 text-sm">
          <InfoRow k="Phone" v={c.phone} />
          <InfoRow k="Email" v={c.email || "—"} />
          <InfoRow k="City" v={c.city || "—"} />
          <InfoRow k="Plan" v={c.plan_type ? `${c.plan_type.toUpperCase()} · ${c.plan_currency || "INR"} ${c.plan_price || 0}` : "—"} />
          <InfoRow k="Plan dates" v={`${c.plan_start || "—"} → ${c.plan_end || "—"}`} />
          <InfoRow k="Next follow-up" v={c.next_followup ? new Date(c.next_followup).toLocaleDateString() : "—"} />
          <InfoRow k="Goal" v={c.goal || "—"} />
          <InfoRow k="Total paid" v={`${c.plan_currency || "INR"} ${data.total_paid}`} highlight />
        </div>

        {/* Dietitian + App Access */}
        <div className="mt-8">
          <div className="section-eyebrow">Team & app</div>
          <div className="mt-3 card-soft p-4">
            <label className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "var(--text-secondary)" }}>Assigned dietitian</label>
            <select data-testid="assign-dietitian" value={c.dietitian_id || ""} onChange={(e) => assignDietitian(e.target.value || null)} className="mt-1.5 w-full h-11 rounded-xl px-3 border-2 bg-transparent text-sm" style={{ borderColor: "var(--border-strong)" }}>
              <option value="">— Unassigned —</option>
              {dietitians.map((d) => <option key={d.id} value={d.id}>{d.name} ({d.email})</option>)}
            </select>

            <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--border-subtle)" }}>
              {c.user_id ? (
                <div>
                  <div className="text-xs font-bold" style={{ color: "#2C633D" }}>✓ Client has app access</div>
                  <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>Login email: {c.email}</div>
                  <button data-testid="revoke-app-access" onClick={revokeAccess} className="mt-3 text-xs font-bold text-red-600">Revoke app access</button>
                </div>
              ) : (
                <div>
                  <div className="text-xs font-bold" style={{ color: "var(--primary)" }}>Give this client access to the mobile app</div>
                  <button data-testid="give-app-access" onClick={() => setShowAppAccess(true)} className="mt-2 btn-primary text-xs">📱 Give app access</button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Status quick actions */}
        <div className="mt-6 flex gap-2 flex-wrap">
          {["active", "paused", "completed", "lost"].map((s) => (
            <button key={s} data-testid={`status-${s}`} onClick={() => updateStatus(s)} className="text-xs font-bold px-3 py-1.5 rounded-full border-2" style={{ borderColor: c.status === s ? "var(--primary)" : "var(--border-strong)", background: c.status === s ? "var(--primary)" : "transparent", color: c.status === s ? "var(--yellow)" : "var(--primary)" }}>
              {s}
            </button>
          ))}
        </div>

        {/* Payments */}
        <div className="mt-10">
          <div className="flex items-center gap-2"><IndianRupee size={16} /><h4 className="font-display text-xl" style={{ color: "var(--primary)" }}>Payments</h4></div>
          <form onSubmit={addPayment} className="mt-3 grid grid-cols-6 gap-2">
            <Input data-testid="pay-amt" type="number" placeholder="Amount" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} className="col-span-2 h-10 rounded-lg" />
            <select data-testid="pay-method" value={payMethod} onChange={(e) => setPayMethod(e.target.value)} className="col-span-2 h-10 rounded-lg px-2 border-2 bg-transparent text-sm" style={{ borderColor: "var(--border-strong)" }}>
              <option value="upi">UPI</option><option value="cash">Cash</option><option value="card">Card</option><option value="bank">Bank</option><option value="other">Other</option>
            </select>
            <button data-testid="pay-submit" className="col-span-2 h-10 rounded-lg font-bold text-sm" style={{ background: "var(--primary)", color: "var(--yellow)" }}>Add</button>
            <Input data-testid="pay-note" value={payNote} onChange={(e) => setPayNote(e.target.value)} placeholder="Note (optional)" className="col-span-6 h-10 rounded-lg" />
          </form>
          <div className="mt-4 space-y-2">
            {data.payments.length === 0 && <div className="text-sm" style={{ color: "var(--text-secondary)" }}>No payments logged yet.</div>}
            {data.payments.map((p) => (
              <div key={p.id} className="p-3 rounded-xl flex items-center justify-between" style={{ background: "var(--bg-paper)" }}>
                <div>
                  <div className="font-bold" style={{ color: "var(--primary)" }}>{p.currency} {p.amount}</div>
                  <div className="text-xs" style={{ color: "var(--text-secondary)" }}>{p.method} · {new Date(p.date).toLocaleDateString()}{p.note ? " · " + p.note : ""}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div className="mt-10">
          <div className="flex items-center gap-2"><StickyNote size={16} /><h4 className="font-display text-xl" style={{ color: "var(--primary)" }}>Notes / History</h4></div>
          <form onSubmit={addNote} className="mt-3 grid grid-cols-6 gap-2">
            <select data-testid="note-type" value={noteType} onChange={(e) => setNoteType(e.target.value)} className="col-span-2 h-10 rounded-lg px-2 border-2 bg-transparent text-sm" style={{ borderColor: "var(--border-strong)" }}>
              <option value="call">Call</option><option value="whatsapp">WhatsApp</option><option value="walkin">Walk-in</option><option value="weight">Weight update</option><option value="note">Note</option>
            </select>
            <Input data-testid="note-text" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="What happened?" className="col-span-3 h-10 rounded-lg" />
            <button data-testid="note-submit" className="h-10 rounded-lg font-bold text-sm" style={{ background: "var(--primary)", color: "var(--yellow)" }}>Add</button>
          </form>
          <div className="mt-4 space-y-2">
            {data.notes.length === 0 && <div className="text-sm" style={{ color: "var(--text-secondary)" }}>No history yet.</div>}
            {data.notes.map((n) => (
              <div key={n.id} className="p-3 rounded-xl" style={{ background: "var(--bg-paper)" }}>
                <div className="text-xs uppercase tracking-widest font-bold" style={{ color: "var(--red)" }}>{n.type}</div>
                <div className="mt-1" style={{ color: "var(--primary)" }}>{n.text}</div>
                <div className="text-[10px] mt-1" style={{ color: "var(--text-secondary)" }}>{new Date(n.created_at).toLocaleString()}{n.by ? " · " + n.by : ""}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Danger */}
        <div className="mt-12 pt-6 border-t" style={{ borderColor: "var(--border-subtle)" }}>
          <button data-testid="client-delete" onClick={() => onDelete(clientId)} className="text-xs font-bold text-red-600 flex items-center gap-1"><Trash2 size={12} /> Delete this client</button>
        </div>
      </div>
      {showAppAccess && (
        <AppAccessModal
          client={c}
          onClose={() => setShowAppAccess(false)}
          onDone={() => { setShowAppAccess(false); load(); }}
          dietitians={dietitians}
        />
      )}
    </div>
  );
}

function AppAccessModal({ client, onClose, onDone, dietitians }) {
  const [email, setEmail] = useState(client.email || "");
  const [password, setPassword] = useState(genPassword());
  const [dietitianId, setDietitianId] = useState(client.dietitian_id || "");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Email required");
    setLoading(true);
    try {
      await api.post("/app/owner/create-client-account", {
        client_id: client.id, email, password, dietitian_id: dietitianId || null,
      });
      toast.success(`App access granted. Share these credentials with ${client.name}`);
      onDone();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const shareText = encodeURIComponent(`Hi ${client.name}, welcome to DietWalaa! 🍉\n\nDownload our app:\nhttps://dietwalaa.com/app\n\nYour login:\nEmail: ${email}\nPassword: ${password}\n\n— Team DietWalaa`);
  const waShare = `https://wa.me/${(client.phone || "").replace(/[^0-9]/g, "")}?text=${shareText}`;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="w-full max-w-md card-soft p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start">
          <div>
            <div className="section-eyebrow">📱 App access</div>
            <h3 className="font-display text-2xl mt-1" style={{ color: "var(--primary)" }}>Create login for {client.name}</h3>
          </div>
          <button onClick={onClose}><X /></button>
        </div>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "var(--text-secondary)" }}>Email</label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-11 rounded-xl" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "var(--text-secondary)" }}>Password (auto-generated, editable)</label>
            <div className="flex gap-2 mt-1">
              <Input required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 rounded-xl flex-1" />
              <button type="button" onClick={() => setPassword(genPassword())} className="text-xs font-bold px-3 rounded-xl border-2" style={{ borderColor: "var(--border-strong)", color: "var(--primary)" }}>↻ New</button>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "var(--text-secondary)" }}>Assign dietitian</label>
            <select value={dietitianId} onChange={(e) => setDietitianId(e.target.value)} className="mt-1 w-full h-11 rounded-xl px-3 border-2 bg-transparent text-sm" style={{ borderColor: "var(--border-strong)" }}>
              <option value="">— Unassigned —</option>
              {dietitians.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div className="pt-2 flex gap-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1">Cancel</button>
            <button disabled={loading} className="btn-primary flex-1 disabled:opacity-60">{loading ? "…" : "Create"}</button>
          </div>
          <a href={waShare} target="_blank" rel="noopener noreferrer" className="btn-ghost w-full text-xs justify-center" style={{ borderColor: "#25D366", color: "#25D366" }}>
            <MessageCircle size={12} /> Send credentials on WhatsApp after creating
          </a>
        </form>
      </div>
    </div>
  );
}

function genPassword() {
  const chars = "abcdefghjkmnpqrstuvwxyz23456789";
  let s = ""; for (let i = 0; i < 8; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return s;
}

function InfoRow({ k, v, highlight }) {
  return (
    <div className="flex justify-between gap-4 py-1.5" style={{ borderBottom: "1px dashed var(--border-subtle)" }}>
      <span className="text-xs uppercase tracking-widest font-bold" style={{ color: "var(--text-secondary)" }}>{k}</span>
      <span className={highlight ? "font-bold" : ""} style={{ color: highlight ? "var(--red)" : "var(--primary)" }}>{v}</span>
    </div>
  );
}

/* -------------------- New Blog Modal -------------------- */
function NewBlogModal({ onClose, onCreated }) {
  const [f, setF] = useState({ title: "", slug: "", excerpt: "", content: "", image_url: "", tags: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/blog", { ...f, tags: f.tags.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Post created"); onCreated();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 overflow-y-auto" style={{ background: "rgba(0,0,0,0.4)" }} onClick={onClose}>
      <div className="w-full max-w-2xl card-soft p-8 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start">
          <div>
            <div className="section-eyebrow">New</div>
            <h3 className="font-display text-3xl mt-1" style={{ color: "var(--primary)" }}>Publish a blog post</h3>
          </div>
          <button onClick={onClose}><X /></button>
        </div>
        <form onSubmit={submit} className="mt-6 grid md:grid-cols-2 gap-4">
          <Input data-testid="blog-title" placeholder="Title" value={f.title} onChange={set("title")} required className="h-11 rounded-xl" />
          <Input data-testid="blog-slug" placeholder="slug-like-this" value={f.slug} onChange={set("slug")} required className="h-11 rounded-xl" />
          <Input data-testid="blog-image" placeholder="Image URL" value={f.image_url} onChange={set("image_url")} className="h-11 rounded-xl md:col-span-2" />
          <Input data-testid="blog-tags" placeholder="Tags (comma separated)" value={f.tags} onChange={set("tags")} className="h-11 rounded-xl md:col-span-2" />
          <Input data-testid="blog-excerpt" placeholder="Short excerpt" value={f.excerpt} onChange={set("excerpt")} required className="h-11 rounded-xl md:col-span-2" />
          <textarea data-testid="blog-content" placeholder="Full content..." value={f.content} onChange={set("content")} required rows={8} className="rounded-xl md:col-span-2 p-3 border" style={{ borderColor: "var(--border-strong)" }} />
          <div className="md:col-span-2 flex gap-3 justify-end">
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
            <button data-testid="blog-create-submit" disabled={loading} className="btn-primary disabled:opacity-60">{loading ? "..." : "Publish"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
